import { router } from '@inertiajs/react'
import { ArrowLeftIcon, UserIcon, SendIcon, MessageCircleIcon } from 'lucide-react'
import { format } from 'date-fns'
import { useState, useEffect, useRef } from 'react'
import * as ActionCable from '@rails/actioncable'

import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

interface Lead {
  id: number
  name: string
  instagram_username: string | null
}

interface InstagramAccount {
  id: number
  username: string
  instagram_user_id: string
  business_type: string
}

interface Conversation {
  id: number
  instagram_thread_id: string
  lead: Lead
  instagram_account: InstagramAccount
}

interface Message {
  id: number
  instagram_message_id: string
  sender_instagram_id: string
  message_text: string
  direction: 'inbound' | 'outbound'
  sent_at: string
  read_at: string | null
}

interface ConversationShowProps {
  conversation: Conversation
  messages: Message[]
}

export default function ConversationShow({ conversation, messages: initialMessages }: ConversationShowProps) {
  const [messages, setMessages] = useState<Message[]>(initialMessages)
  const [messageText, setMessageText] = useState('')
  const [isSending, setIsSending] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const cableRef = useRef<ActionCable.Cable | null>(null)
  const channelRef = useRef<ActionCable.Channel | null>(null)

  const handleBack = () => {
    router.visit('/instagram/conversations')
  }

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Set up ActionCable connection
  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    if (!token) {
      console.error('[ActionCable] No auth token found')
      return
    }

    // Create cable connection
    const cable = ActionCable.createConsumer(`/cable?token=${token}`)
    cableRef.current = cable

    // Subscribe to conversation channel
    const channel = cable.subscriptions.create(
      {
        channel: 'ConversationChannel',
        conversation_id: conversation.id,
      },
      {
        connected() {
          console.log('[ActionCable] Connected to conversation', conversation.id)
        },
        disconnected() {
          console.log('[ActionCable] Disconnected from conversation', conversation.id)
        },
        received(data: { type: string; message: Message }) {
          console.log('[ActionCable] Received data:', data)
          if (data.type === 'new_message') {
            setMessages((prev) => [...prev, data.message])
          }
        },
      }
    )
    channelRef.current = channel

    // Cleanup on unmount
    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe()
      }
      if (cableRef.current) {
        cableRef.current.disconnect()
      }
    }
  }, [conversation.id])

  const handleSendMessage = async () => {
    if (!messageText.trim() || isSending) return

    setIsSending(true)

    try {
      // Use direct fetch instead of Inertia router to avoid modal popup
      const token = localStorage.getItem('auth_token')
      const response = await fetch(`/instagram/conversations/${conversation.id}/send_message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ message_text: messageText.trim() }),
      })

      if (response.ok) {
        setMessageText('')
        // Message will appear via ActionCable broadcast
      } else {
        console.error('[SendMessage] Failed:', response.status, response.statusText)
      }
    } catch (error) {
      console.error('[SendMessage] Error:', error)
    } finally {
      setIsSending(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const formatMessageTime = (timestamp: string) => {
    try {
      return format(new Date(timestamp), 'MMM d, yyyy h:mm a')
    } catch {
      return ''
    }
  }

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <div className="@container/main flex flex-1 flex-col min-w-0">
        {/* Header - Fixed */}
        <div className="flex-shrink-0 border-b bg-background px-4 py-4 lg:px-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              data-testid="back-button"
            >
              <ArrowLeftIcon className="size-5" />
            </Button>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Avatar className="size-10">
                <AvatarFallback className="bg-gradient-to-br from-[#5589ed] to-[#4478dc] text-white">
                  {getInitials(conversation.lead.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold truncate">{conversation.lead.name}</h1>
                  <Badge variant="outline" className="bg-blue-100 text-blue-700">
                    Shop Lead
                  </Badge>
                </div>
                {conversation.lead.instagram_username && (
                  <p className="text-sm text-muted-foreground truncate">
                    @{conversation.lead.instagram_username}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
            <UserIcon className="size-4" />
            <span>Via @{conversation.instagram_account.username}</span>
            <Badge variant="outline" className="ml-2 bg-blue-100 text-blue-700">
              Shop
            </Badge>
          </div>
        </div>

        {/* Messages Container - Scrollable with fixed height */}
        <div className="flex-1 overflow-y-auto px-4 lg:px-6 min-w-0" style={{ maxHeight: 'calc(100vh - 280px)' }}>
          <div className="py-6">
            {messages.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center text-muted-foreground">
                  <MessageCircleIcon className="mx-auto mb-4 size-12 text-muted-foreground" />
                  <p>No messages in this conversation yet</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => {
                  const isOutbound = message.direction === 'outbound'

                  return (
                    <div
                      key={message.id}
                      className={cn(
                        'flex',
                        isOutbound ? 'justify-end' : 'justify-start'
                      )}
                      data-testid={`message-${message.id}`}
                    >
                      <div
                        className={cn(
                          'max-w-[70%] rounded-lg px-4 py-2',
                          isOutbound
                            ? 'bg-[#5589ed] text-white'
                            : 'bg-muted text-foreground'
                        )}
                      >
                        <p className="whitespace-pre-wrap break-words">
                          {message.message_text}
                        </p>
                        <p
                          className={cn(
                            'mt-1 text-xs',
                            isOutbound ? 'text-blue-100' : 'text-muted-foreground'
                          )}
                        >
                          {formatMessageTime(message.sent_at)}
                        </p>
                      </div>
                    </div>
                  )
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>

        {/* Message Input - Fixed at bottom */}
        <div className="flex-shrink-0 border-t bg-background px-4 py-4 lg:px-6">
          <div className="flex gap-3">
            <Textarea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message to the lead..."
              className="min-h-[80px] resize-none"
              disabled={isSending}
              data-testid="message-input"
            />
            <Button
              onClick={handleSendMessage}
              disabled={!messageText.trim() || isSending}
              size="icon"
              className="bg-[#5589ed] hover:bg-[#4478dc] text-white h-[80px] w-[80px] shrink-0 shadow-lg shadow-[#5589ed]/20"
              data-testid="send-button"
            >
              <SendIcon className="size-5" />
            </Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>
    </div>
  )
}
